// src/components/dashboard/PrescriptionGrid.tsx
import React from 'react';
import { Pill } from 'lucide-react';

interface PrescriptionListItem {
  id: string;
  doctor_name?: string;
  hospital?: string;
  reason_grouping?: string;
  prescribed_date?: string;
}

interface PrescriptionGridProps {
  prescriptions: PrescriptionListItem[];
  onSelectPrescription: (prescriptionId: string) => void;
}

export const PrescriptionGrid: React.FC<PrescriptionGridProps> = ({
  prescriptions,
  onSelectPrescription
}) => {
  return (
    <div style={{
      fontFamily: 'var(--font-primary)',
      padding: 'clamp(20px, 5vw, 40px)',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%'
    }}>
      <h1 style={{
        fontSize: 'clamp(24px, 5vw, 32px)',
        fontWeight: 700,
        marginBottom: 'clamp(20px, 4vw, 32px)',
        color: 'var(--color-text)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <Pill size={32} strokeWidth={2.5} />
        Prescriptions
      </h1>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
        gap: 'clamp(16px, 3vw, 24px)'
      }}>
        {prescriptions.map((prescription) => (
          <div
            key={prescription.id}
            onClick={() => onSelectPrescription(prescription.id)}
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '2px solid var(--color-border)',
              borderRadius: '12px',
              padding: 'clamp(16px, 4vw, 24px)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: 'var(--shadow-sm)',
              minHeight: '150px',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            <h3 style={{
              fontSize: 'clamp(16px, 3vw, 18px)',
              fontWeight: 600,
              color: 'var(--color-text)',
              margin: '0 0 8px 0',
              wordBreak: 'break-word'
            }}>
              {prescription.doctor_name ? ` ${prescription.doctor_name}` : 'Prescription'}
            </h3>

            {prescription.hospital && (
              <p style={{
                fontSize: 'clamp(13px, 2.5vw, 14px)',
                color: 'var(--color-secondary)',
                margin: '0 0 8px 0',
                wordBreak: 'break-word',
                lineHeight: 1.4
              }}>
                {prescription.hospital}
              </p>
            )}

            {prescription.reason_grouping && (
              <div style={{
                display: 'inline-block',
                padding: '6px 12px',
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-background)',
                borderRadius: '16px',
                fontSize: 'clamp(11px, 2vw, 12px)',
                fontWeight: 600,
                margin: '0 0 12px 0',
                maxWidth: 'fit-content'
              }}>
                {prescription.reason_grouping}
              </div>
            )}

            {prescription.prescribed_date && (
              <p style={{
                fontSize: 'clamp(12px, 2vw, 13px)',
                color: 'var(--color-muted)',
                margin: '0',
                marginTop: 'auto'
              }}>
                {new Date(prescription.prescribed_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
